import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { TenantUserService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { TenantUser, Location } from "@/utils/types/tenantUser";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Form, Input, DatePicker, Select } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CustomImageUpload } from "@/components/CustomImageUpload";

const UserFormPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dateFormat = import.meta.env.VITE_DATE_FORMAT;
  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressDetails, setAddressDetails] = useState<Location | null>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);

  // Use React Query for mutations
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      if (values.date_of_birth) {
        values.date_of_birth = values.date_of_birth.format("YYYY-MM-DD");
      }

      // 🧠 Extract location-related fields and nest them under `location`
      const payload: TenantUser = {
        ...values,
        address: undefined,
        profile_picture: profilePicture ?? "",
        location: {
          latitude: addressDetails?.latitude ?? "",
          longitude: addressDetails?.longitude ?? "",
          place_id: addressDetails?.place_id ?? "",
          name: addressDetails?.name ?? "",
          display_name: addressDetails?.display_name ?? "",
          country: addressDetails?.country ?? "",
          state: addressDetails?.state ?? "",
          district: addressDetails?.district ?? "",
          city: addressDetails?.city ?? "",
          street: addressDetails?.street ?? "",
          postal_code: addressDetails?.postal_code ?? "",
          phone: addressDetails?.phone ?? "",
        },
      };

      if (isEditMode) {
        // Check for changes before updating
        if (!checkFormChanges(payload, user ?? {}, notify)) {
          setIsSubmitting(false);
          return;
        }

        const response = await TenantUserService.updateUser(id ?? "", payload);
        if (response) {
          navigate(navigationRoutes.USERS);
        }
      } else {
        const response = await TenantUserService.createUser(payload);
        if (response) {
          navigate(navigationRoutes.USERS);
        }
      }
    } catch (error) {
      console.error("Validation failed:", error);
      // Ant Design will show validation errors automatically
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use React Query for data fetching
  const { data: user, isLoading } = useQuery({
    queryKey: ["user", id],
    queryFn: () => TenantUserService.getTenantUserById(id ?? ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (isEditMode && user) {
      const locationDisplayName = user.user.location?.display_name ?? "";
      setAddressDetails(user.user.location || null); // preserve full location object

      form.setFieldsValue({
        ...user.user,
        address: locationDisplayName, // safe fallback to empty string
      });
    }
  }, [isEditMode, user, form]);

  // Handle address selection
  const handleAddressChange = (details?: Location) => {
    if (details) {
      form.setFieldsValue({
        // Location coordinates
        address: details.formatted_address,
      });
      setAddressDetails(details);
    } else {
      form.setFieldsValue({
        address: "",
      });
      setAddressDetails(null);
    }
  };

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !user) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit User" : "Add User"}
          onBack={() => navigate(navigationRoutes.USERS)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={isEditMode ? "Save User Changes" : "Save New User"}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* Existing Fields */}
        <Form.Item
          name="profile_picture"
          label="Profile Picture"
          valuePropName="fileList"
          getValueFromEvent={({ fileList }) => fileList}
          style={{ width: "100%" }}
        >
          <CustomImageUpload
            shape="square"
            borderStyle="dashed"
            accept="image/*"
            className="w-full"
            initialUrl={profilePicture ?? undefined}
            onUploaded={response => {
              const filePath = response[0].file_path;
              setProfilePicture(filePath);
            }}
            boxWidth={200}
            boxHeight={100}
          />
        </Form.Item>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Form.Item
              name="email"
              label="Email"
              style={{ width: "100%" }}
              rules={[
                { required: true, message: "Please enter user email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email" />
            </Form.Item>

            <Form.Item
              name="first_name"
              label="First Name"
              style={{ width: "100%" }}
              rules={[{ required: true, message: "Please enter first name" }]}
            >
              <Input placeholder="Enter first name" className="w-full" />
            </Form.Item>

            <Form.Item
              name="last_name"
              label="Last Name"
              style={{ width: "100%" }}
            >
              <Input placeholder="Enter last name" className="w-full" />
            </Form.Item>

            <Form.Item
              name="full_name"
              label="Full Name"
              style={{ width: "100%" }}
            >
              <Input placeholder="Enter full name" className="w-full" />
            </Form.Item>

            <Form.Item
              name="phone_number"
              label="Phone Number"
              style={{ width: "100%" }}
            >
              <Input placeholder="Enter phone number" className="w-full" />
            </Form.Item>

            {/* Google Address Autocomplete Field */}
            <Form.Item name="address" label="Address" style={{ width: "100%" }}>
              <GoogleAddressInput
                value={form.getFieldValue("address")}
                placeholder="Enter your address"
                className="w-full"
                onChange={handleAddressChange}
                countryRestriction="in" // Restrict to India, change as needed
                types={["establishment", "geocode"]} // Include businesses and addresses
              />
            </Form.Item>

            {/* New Fields */}
            <Form.Item name="gender" label="Gender" style={{ width: "100%" }}>
              <Select placeholder="Select gender" className="w-full">
                <Select.Option value="male">Male</Select.Option>
                <Select.Option value="female">Female</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="date_of_birth"
              label="Date of Birth"
              style={{ width: "100%" }}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Select date of birth"
                format={dateFormat}
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="mobile_number"
              label="Mobile Number"
              style={{ width: "100%" }}
              className="w-full"
            >
              <Input
                placeholder="Enter mobile number"
                addonBefore="+91"
                className="w-full"
              />
            </Form.Item>

            <Form.Item
              name="username"
              label="Username"
              style={{ width: "100%" }}
            >
              <Input placeholder="Enter username" className="w-full" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              className="w-full"
              rules={[
                { min: 6, message: "Password should be at least 6 characters" },
              ]}
            >
              <Input.Password placeholder="Enter password" className="w-full" />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default UserFormPage;
